/*
 * Copyright 2025 Intuition Robotics ltd
 */
/*
 * A typescript & react boilerplate with api call example
 *
 * Copyright (C) 2020 Adam van der Kruk aka TacB0sS
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import * as emotion from 'emotion';
import {COLORS} from "@res/colors";

export const _margin = (px: number) => emotion.css`margin: ${px}px;`;
export const _marginTop = (px: number) => emotion.css`margin-top: ${px}px;`;
export const _marginBottom = (px: number) => emotion.css`margin-bottom: ${px}px;`;
export const _marginLeft = (px: number) => emotion.css`margin-left: ${px}px;`;
export const _marginRight = (px: number) => emotion.css`margin-right: ${px}px;`;
export const _padding = (px: number) => emotion.css`padding: ${px}px;`;
export const _paddingLeft = (px: number) => emotion.css`padding-left: ${px}px;`;
export const _paddingRight = (px: number) => emotion.css`padding-right: ${px}px;`;
export const _paddingTop = (px: number) => emotion.css`padding-top: ${px}px;`;
export const _paddingBottom = (px: number) => emotion.css`padding-bottom: ${px}px;`;
export const _backgroundColor = (color: string) => emotion.css`background-color: ${color};`;
export const _color = (color: keyof typeof COLORS | string) => emotion.css`color: ${color};`;


export enum Spacing {
    xs = 4,
    s = 8,
    m = 16,
    l = 32,
    xl = 64
}

export const margin_r_sm = _marginRight(Spacing.s);
export const margin_l_sm = _marginLeft(Spacing.s);
export const margin_r_med = _marginRight(Spacing.m);
export const margin_l_med = _marginLeft(Spacing.m);
export const margin_r_lg = _marginRight(Spacing.l);
export const margin_l_lg = _marginLeft(Spacing.l);

export const margin_t_sm = _marginTop(Spacing.s);
export const margin_b_sm = _marginBottom(Spacing.s);
export const margin_t_med = _marginTop(Spacing.m);
export const margin_b_med = _marginBottom(Spacing.m);
export const margin_t_lg = _marginTop(Spacing.l);
export const margin_b_lg = _marginBottom(Spacing.l);

export const margin_rl_sm = `${margin_r_sm} ${margin_l_sm}`;
export const margin_tb_sm = `${margin_t_sm} ${margin_b_sm}`;
export const margin_rl_med = `${margin_r_med} ${margin_l_med}`;
export const margin_tb_med = `${margin_t_med} ${margin_b_med}`;
export const margin_rl_lg = `${margin_r_lg} ${margin_l_lg}`;
export const margin_tb_lg = `${margin_t_lg} ${margin_b_lg}`;

export const padding_r_sm = _paddingRight(Spacing.s);
export const padding_l_sm = _paddingLeft(Spacing.s);
export const padding_r_med = _paddingRight(Spacing.m);
export const padding_l_med = _paddingLeft(Spacing.m);
export const padding_r_lg = _paddingRight(Spacing.l);
export const padding_l_lg = _paddingLeft(Spacing.l);

export const padding_t_sm = _paddingTop(Spacing.s);
export const padding_b_sm = _paddingBottom(Spacing.s);
export const padding_t_med = _paddingTop(Spacing.m);
export const padding_b_med = _paddingBottom(Spacing.m);
export const padding_t_lg = _paddingTop(Spacing.l);
export const padding_b_lg = _paddingBottom(Spacing.l);

export const padding_rl_sm = `${padding_r_sm} ${padding_l_sm}`;
export const padding_tb_sm = `${padding_t_sm} ${padding_b_sm}`;
export const padding_rl_med = `${padding_r_med} ${padding_l_med}`;
export const padding_tb_med = `${padding_t_med} ${padding_b_med}`;
export const padding_rl_lg = `${padding_r_lg} ${padding_l_lg}`;
export const padding_tb_lg = `${padding_t_lg} ${padding_b_lg}`;

export const inputTextField = emotion.css(
    {
        boxSizing: "border-box",
        border: `1px solid ${COLORS.neutral_gray_2}`,
        borderRadius: 4,
        fontSize: "1.2rem",
        padding: "0.3rem 0.5rem",
        width: 250
    }
);
